import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import {
    Box,
    Button,
    Typography,
    TextField,
} from '@mui/material';

import GoogleIcon from '@mui/icons-material/Google';

import ParticlesComponent from './Particles';
import axios from '../../services/axios';

const Login = () => {
    const navigate = useNavigate();

    // ==========================================
    // ESTADOS
    // ==========================================
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [searchParams, setSearchParams] = useSearchParams();

    // ==========================================
    // LOGIN CON GOOGLE
    // ==========================================
    const handleGoogleLogin = () => {
        window.location.href =
            'https://turismosanroman.com/turismobackend/public/login/google';
    };

    // ==========================================
    // LOGIN TRADICIONAL
    // ==========================================
    const handleLogin = async () => {
        setError('');
        setSuccessMessage('');

        // Validar campos
        if (!email.trim() || !password) {
            setError(
                'Ingresa tu correo electrónico y contraseña.'
            );
            return;
        }

        try {
            const response = await axios.post('/login', {
                email: email.trim(),
                password,
            });

            const data = response.data;

            console.log('Login exitoso:', data);

            // ==========================================
            // GUARDAR DATOS DE SESIÓN
            // ==========================================
            sessionStorage.setItem('token', data.token);
            sessionStorage.setItem('name', data.name || '');
            sessionStorage.setItem('role', data.role || '');
            sessionStorage.setItem('email', data.email || email);

            // Eliminar mensaje de sesión expirada
            sessionStorage.removeItem('sessionExpired');

            // ==========================================
            // IR AL INICIO
            // ==========================================
            navigate('/');

        } catch (err) {
            console.error('Error en login:', err);

            const data = err.response?.data;

            // ==========================================
            // EMAIL NO VERIFICADO
            // ==========================================
            if (
                data?.error === 'Tu email no está verificado.'
            ) {
                setError(
                    'Debes verificar tu correo antes de iniciar sesión.'
                );
                return;
            }

            // ==========================================
            // ERRORES DE VALIDACIÓN
            // ==========================================
            if (data?.errors) {
                const validationErrors = Object.values(
                    data.errors
                ).flat();

                if (validationErrors.length > 0) {
                    setError(validationErrors[0]);
                    return;
                }
            }

            // ==========================================
            // ERROR DEL BACKEND
            // ==========================================
            if (data?.message) {
                setError(data.message);
                return;
            }

            // ==========================================
            // ERROR DE CONEXIÓN
            // ==========================================
            if (!err.response) {
                setError(
                    'No se pudo conectar con el servidor.'
                );
                return;
            }

            // ==========================================
            // ERROR GENÉRICO
            // ==========================================
            setError(
                'Credenciales incorrectas o error de conexión.'
            );
        }
    };

    // ==========================================
    // ENTER PARA INICIAR SESIÓN
    // ==========================================
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleLogin();
        }
    };

    // ==========================================
    // MENSAJES DE VERIFICACIÓN Y SESIÓN
    // ==========================================
    useEffect(() => {

        // ------------------------------------------
        // CORREO VERIFICADO
        // ------------------------------------------
        if (searchParams.get('verified') === '1') {
            setSuccessMessage(
                '✅ Correo verificado correctamente. Ya puedes iniciar sesión.'
            );

            // Limpiar ?verified=1
            setSearchParams({});
        }

        // ------------------------------------------
        // SESIÓN EXPIRADA
        // ------------------------------------------
        if (
            sessionStorage.getItem('sessionExpired') === 'true'
        ) {
            setError(
                'Tu sesión ha expirado por inactividad. Por favor, vuelve a iniciar sesión.'
            );

            sessionStorage.removeItem('sessionExpired');
        }

    }, [searchParams, setSearchParams]);

    // ==========================================
    // INTERFAZ
    // ==========================================
    return (
        <Box sx={styles.container}>

            {/* =====================================
                IMAGEN LATERAL
            ====================================== */}
            <Box sx={styles.imageContainer} />

            {/* =====================================
                PARTICULAS
            ====================================== */}
            <ParticlesComponent
                id="tsparticles"
                style={styles.particles}
            />

            {/* =====================================
                FORMULARIO
            ====================================== */}
            <Box sx={styles.formContainer}>

                <Box sx={styles.form}>

                    {/* =================================
                        TÍTULO
                    ================================== */}
                    <Typography
                        variant="h4"
                        sx={styles.title}
                    >
                        Inicio de Sesión
                    </Typography>

                    {/* =================================
                        MENSAJE CORREO VERIFICADO
                    ================================== */}
                    {successMessage && (
                        <Alert
                            severity="success"
                            sx={{ mb: 2 }}
                        >
                            {successMessage}
                        </Alert>
                    )}

                    {/* =================================
                        CORREO
                    ================================== */}
                    <TextField
                        label="Correo Electrónico"
                        fullWidth
                        margin="normal"
                        value={email}
                        onChange={(e) =>
                            setEmail(e.target.value)
                        }
                        onKeyDown={handleKeyDown}
                        autoComplete="email"
                    />

                    {/* =================================
                        CONTRASEÑA
                    ================================== */}
                    <TextField
                        label="Contraseña"
                        fullWidth
                        type="password"
                        margin="normal"
                        value={password}
                        onChange={(e) =>
                            setPassword(e.target.value)
                        }
                        onKeyDown={handleKeyDown}
                        autoComplete="current-password"
                    />

                    {/* =================================
                        ERROR
                    ================================== */}
                    {error && (
                        <Typography
                            color="error"
                            sx={{
                                mt: 1,
                                mb: 1,
                                textAlign: 'center',
                            }}
                        >
                            {error}
                        </Typography>
                    )}

                    {/* =================================
                        BOTÓN LOGIN
                    ================================== */}
                    <Button
                        variant="contained"
                        fullWidth
                        sx={{
                            ...styles.button,
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#1565c0',
                            },
                        }}
                        onClick={handleLogin}
                    >
                        Iniciar sesión
                    </Button>

                    {/* =================================
                        RECUPERAR CONTRASEÑA
                    ================================== */}
                    <Typography
                        sx={{
                            mt: 1,
                            textAlign: 'center',
                        }}
                    >
                        <Link to="/forgot-password">
                            ¿Olvidaste tu contraseña?
                        </Link>
                    </Typography>

                    {/* =================================
                        REGISTRO
                    ================================== */}
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 2,
                            textAlign: 'center',
                        }}
                    >
                        ¿No tienes cuenta?{' '}

                        <Link to="/register">
                            Regístrate aquí
                        </Link>
                    </Typography>

                    {/* =================================
                        SEPARADOR
                    ================================== */}
                    <Typography
                        sx={{
                            textAlign: 'center',
                            mt: 2,
                            mb: 2,
                        }}
                    >
                        o continúa con:
                    </Typography>

                    {/* =================================
                        GOOGLE
                    ================================== */}
                    <Button
                        variant="contained"
                        fullWidth
                        sx={{
                            ...styles.button,
                            backgroundColor: '#DB4437',
                            '&:hover': {
                                backgroundColor: '#C53929',
                            },
                        }}
                        onClick={handleGoogleLogin}
                        startIcon={<GoogleIcon />}
                    >
                        Google
                    </Button>

                </Box>
            </Box>
        </Box>
    );
};

// ==========================================
// ESTILOS
// ==========================================
const styles = {

    container: {
        display: 'flex',
        height: '100vh',
        width: '100%',
    },

    imageContainer: {
        flex: 0.5,

        backgroundImage:
            'url(https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRbJYJun3zdQESqfgIUZQZEsBnpI5AChIfOKQ&s)',

        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',

        zIndex: 1200,
    },

    formContainer: {
        flex: 1,

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        position: 'relative',

        overflow: 'auto',
    },

    form: {
        width: '100%',
        maxWidth: '400px',

        padding: '2rem',

        backgroundColor: '#fff',

        borderRadius: '8px',

        boxShadow:
            '0 0 10px rgba(0, 0, 0, 0.1)',

        position: 'relative',

        zIndex: 1,
    },

    title: {
        marginBottom: '2rem',

        color: '#333',

        textAlign: 'center',
    },

    button: {
        marginBottom: '1rem',

        color: '#fff',

        textTransform: 'none',

        fontWeight: 'bold',
    },

    particles: {
        position: 'absolute',

        top: 0,
        left: 0,

        width: '100%',
        height: '100%',

        zIndex: 0,
    },
};

export default Login;